/**
 * Shipments API Types & Schemas
 * Anti-corruption layer between API contracts and domain models
 */

import { z } from '@spcc/core'

// ===========================
// Zod Schemas
// ===========================

// Status enum
export const shipmentStatusSchema = z.enum([
  'scheduled',
  'loading',
  'delivered',
  'cancelled',
])

// Localized text
export const localizedTextSchema = z.object({
  en: z.string(),
  ar: z.string().optional(),
})

// Quantity/Weight
export const measurementSchema = z.object({
  value: z.number(),
  unit: z.string(),
})

// Factory DTO (from api::factory.factory)
export const factoryDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  code: z.string(),
  name: z.string(),
})

// Shipment DTO
export const shipmentDtoSchema = z.object({
  id: z.string().uuid(),
  shipmentNumber: z.string(),
  orderId: z.string().uuid(),
  orderNumber: z.string(),
  product: localizedTextSchema,
  quantity: measurementSchema,
  status: shipmentStatusSchema,
  scheduledDate: z.string().datetime(),
  deliveryDate: z.string().datetime().optional(),
  origin: z.string(),
  destination: z.string(),
  driverId: z.string().uuid().optional(),
  driverName: z.string().optional(),
  truckId: z.string().uuid().optional(), resource: z.string().optional(),
  truckNumber: z.string().optional(),
  weight: measurementSchema.optional(),
  trackingUrl: z.string().url().optional(),
  eta: z.string().datetime().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

// Shipment stats DTO
export const shipmentStatsDtoSchema = z.object({
  scheduled: z.number().int().nonnegative(),
  loading: z.number().int().nonnegative(),
  delivered: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
})

// List response
export const shipmentsListResponseSchema = z.object({
  data: z.array(shipmentDtoSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive().optional(),
  pageSize: z.number().int().positive().optional(),
})

// Detail response
export const shipmentDetailResponseSchema = z.object({
  data: shipmentDtoSchema,
})

// Stats response
export const shipmentStatsResponseSchema = z.object({
  data: shipmentStatsDtoSchema,
})

// ===========================
// Request Schemas
// ===========================

// Create shipment request
export const createShipmentRequestSchema = z.object({
  orderId: z.string().uuid(),
  scheduledDate: z.string().datetime(),
  origin: z.string().min(1),
  destination: z.string().min(1),
  driverId: z.string().uuid().optional(),
  truckId: z.string().uuid().optional(), resource: z.string().optional(),
})

// Update shipment request
export const updateShipmentRequestSchema = z.object({
  scheduledDate: z.string().datetime().optional(),
  status: shipmentStatusSchema.optional(),
  deliveryDate: z.string().datetime().optional(),
  driverId: z.string().uuid().optional(),
  truckId: z.string().uuid().optional(), resource: z.string().optional(),
  weight: measurementSchema.optional(),
  eta: z.string().datetime().optional(),
})

// List query params
export const shipmentsListQuerySchema = z.object({
  status: shipmentStatusSchema.optional(),
  search: z.string().optional(),
  dateFrom: z.string().datetime().optional(),
  dateTo: z.string().datetime().optional(),
  page: z.number().int().positive().optional(),
  pageSize: z.number().int().positive().optional(),
})

// ===========================
// TypeScript Types (inferred from schemas)
// ===========================

export type FactoryDto = z.infer<typeof factoryDtoSchema>
export type ShipmentDto = z.infer<typeof shipmentDtoSchema>
export type ShipmentStatsDto = z.infer<typeof shipmentStatsDtoSchema>
export type ShipmentsListResponse = z.infer<typeof shipmentsListResponseSchema>
export type ShipmentDetailResponse = z.infer<typeof shipmentDetailResponseSchema>
export type ShipmentStatsResponse = z.infer<typeof shipmentStatsResponseSchema>

export type CreateShipmentRequest = z.infer<typeof createShipmentRequestSchema>
export type UpdateShipmentRequest = z.infer<typeof updateShipmentRequestSchema>
export type ShipmentsListQuery = z.infer<typeof shipmentsListQuerySchema>

// ===========================
// Validation Helpers
// ===========================

export function validateShipmentDto(data: unknown): ShipmentDto {
  return shipmentDtoSchema.parse(data)
}

export function validateShipmentsList(data: unknown): ShipmentsListResponse {
  return shipmentsListResponseSchema.parse(data)
}

export function validateShipmentDetail(data: unknown): ShipmentDetailResponse {
  return shipmentDetailResponseSchema.parse(data)
}

export function validateShipmentStats(data: unknown): ShipmentStatsResponse {
  return shipmentStatsResponseSchema.parse(data)
}

export function validateCreateShipmentRequest(data: unknown): CreateShipmentRequest {
  return createShipmentRequestSchema.parse(data)
}

export function validateUpdateShipmentRequest(data: unknown): UpdateShipmentRequest {
  return updateShipmentRequestSchema.parse(data)
}

export function validateShipmentsListQuery(data: unknown): ShipmentsListQuery {
  return shipmentsListQuerySchema.parse(data)
}

// ===========================
// API Endpoints
// ===========================

export const SHIPMENT_ENDPOINTS = {
  LIST: '/api/shipments',
  DETAIL: (id: string) => `/api/shipments/${id}`,
  CREATE: '/api/shipments',
  UPDATE: (id: string) => `/api/shipments/${id}`,
  DELETE: (id: string) => `/api/shipments/${id}`,
  STATS: '/api/shipments/stats',
  TRACK: (id: string) => `/api/shipments/${id}/track`,
} as const
